import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADMIN, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';

const StoryManagementModal = NiceModal.create(({ ID }) => {
	const dispatch = useDispatch();
	const modal = useModal();
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();

	const [loading, setLoading] = useState(false);

	const defaultColSpan = {
		md: 6
	};

	const schema = useMemo(
		() =>
			yup.object().shape({
				title: yup.string().nullable(),
				status: yup.string().nullable(),
				image: yup.string().nullable(),
				expiryDate: yup.string().nullable(),
				priority: yup.number().nullable().required(),
				storyDataJSON: yup.array().of(
					yup.object().shape({
						title: yup.string().nullable(),
						content: yup.string().nullable(),
						buttonTitle: yup.string().nullable(),
						storyTypes: yup.string().nullable().required(),
						actionUrl: yup.string().nullable().required(),
						url: yup.string().nullable().required()
					})
				)
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			title: null,
			status: 'new',
			image: null,
			expiryDate: null,
			priority: null,
			storyDataJSON: [
				{
					title: null,
					content: null,
					buttonTitle: null,
					storyTypes: null,
					actionUrl: null,
					url: null
				}
			]
		}
	});
	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { isDirty }
	} = formMethods;

	const { append, remove } = useFieldArray({
		control: formMethods.control,
		name: 'storyDataJSON'
	});

	const fillForm = () => {
		setLoading(true);
		axios
			.post(ADMIN.STORY.GET_STORY, {
				ID
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						title: data.title ?? null,
						status: data.status ?? null,
						image: data.image ?? null,
						expiryDate: data.expiryDate ?? null,
						priority: data.priority ?? null,
						storyDataJSON: data.storyDataJSON ?? null
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => AppLogger.error(error))
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		if (ID) {
			fillForm();
		}
	}, [ID]);

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			const response = await axios.post(ID ? ADMIN.STORY.UPDATE_STORY : ADMIN.STORY.ADD_STORY, {
				ID,
				...data
			});
			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.STORY_MANAGEMENT));
				showSuccess();
				modal.remove();
			}
		} catch (error) {
			if (error.response?.data.code === 'error.F010') {
				showError({
					message: 'errorMsg.priorityMustBeUnique'
				});
			} else {
				showError();
				AppLogger.error(error);
			}
		} finally {
			setLoading(false);
		}
	};

	const columns = useMemo(
		() => [
			{
				header: 'title',
				accessorKey: 'title',
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].title`} viewMode />,
				editCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].title`} />,
				showOnMobile: true
			},
			{
				header: 'content',
				accessorKey: 'content',
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].content`} viewMode />,
				editCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].content`} />,
				showOnMobile: true
			},
			{
				header: 'buttonTitle',
				accessorKey: 'buttonTitle',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<StringInput name={`storyDataJSON[${index}].buttonTitle`} viewMode />
				),
				editCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].buttonTitle`} />,
				showOnMobile: true
			},
			{
				header: 'storyTypes',
				accessorKey: 'storyTypes',
				size: 100,
				required: true,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						name={`storyDataJSON[${index}].storyTypes`}
						enumName={ENUMS.ENUM_TYPES}
						value={watch(`storyDataJSON[${index}].storyTypes`)}
						viewMode
						setValue={(value) => {
							setValue(`storyDataJSON[${index}].storyTypes`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				),
				editCell: ({ row: { index } }) => (
					<EnumSelect
						name={`storyDataJSON[${index}].storyTypes`}
						enumName={ENUMS.STORY_TYPES}
						value={watch(`storyDataJSON[${index}].storyTypes`)}
						setValue={(value) => {
							setValue(`storyDataJSON[${index}].storyTypes`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'actionUrl',
				accessorKey: 'actionUrl',
				required: true,
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].actionUrl`} viewMode />,
				editCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].actionUrl`} />,
				showOnMobile: true
			},
			{
				header: 'url',
				accessorKey: 'url',
				required: true,
				size: 100,
				renderCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].url`} viewMode />,
				editCell: ({ row: { index } }) => <StringInput name={`storyDataJSON[${index}].url`} />,
				showOnMobile: true
			},

			{
				id: 'editRow',
				size: 20,
				maxSize: 20,
				showOnMobile: true
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20,
				showOnMobile: true
			}
		],
		[]
	);
	return (
		<Modal fullwidth maxWidth="large" className="modal-storyManagement">
			<ModalTitle>
				<Typography variant="p1">storyManagementModal</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="title" isLoading={loading} required>
						<StringInput name="title" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="status" isLoading={loading}>
						<StringInput name="status" disabled />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="image" isLoading={loading} required>
						<ImageInput
							value={watch('image')}
							setValue={(value) => {
								setValue('image', value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							placeholder="selectImage"
							name="image"
							acceptedFileType="image/*"
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="expiryDate" isLoading={loading}>
						<DateInput
							name="expiryDate"
							value={watch('expiryDate')}
							setValue={(value) => {
								setValue('expiryDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="priority" isLoading={loading} required>
						<IntegerInput name="priority" />
					</FormItem>

					<Table
						data={watch('storyDataJSON')}
						columns={columns}
						onRowDelete={({ index }) => remove(index)}
						onRowAdd={() => {
							append({});
						}}
						initEditMode={() => true}
						footerComponent={
							<Button
								className="mx-3"
								variant="blackOutline"
								label="addNewRow"
								icon={faPlusCircle}
								onClick={() => {
									append({});
								}}
							/>
						}
					/>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					variant="outline"
					onClick={() => {
						modal.remove();
					}}
				/>
				<Button
					label="save"
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					loading={loading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default StoryManagementModal;
